import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  fetchEquityCurve, fetchModelDetail, updateTradingModel,
  type PredictionRow,
} from "../api/client";
import { TOOLTIPS } from "../lib/tooltips";
import { signalColor, signalFor } from "../lib/decisions";
import { HelpTooltip } from "../lib/HelpTooltip";

function fmtPct(v: number | undefined | null) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}

// Color the state badge so paper-live / live / retired are scannable.
function stateColors(state: string | null | undefined): { bg: string; fg: string; border: string } {
  switch (state) {
    case "live":       return { bg: "#14532d", fg: "#86efac", border: "#16a34a" };
    case "paper-live": return { bg: "#1e3a8a", fg: "#bfdbfe", border: "#3b82f6" };
    case "retired":    return { bg: "#3f3f46", fg: "#a1a1aa", border: "#52525b" };
    default:           return { bg: "#1e293b", fg: "#cbd5e1", border: "#334155" };
  }
}

export function ModelDetailPage() {
  const { modelId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stateBanner, setStateBanner] = useState<{ ok: boolean; msg: string } | null>(null);
  const detail = useQuery({
    // Bumped from 50 → 500 so the decisions-over-time chart has enough points
    // to read as a price trajectory.
    queryKey: ["model-detail", modelId],
    queryFn: () => fetchModelDetail(modelId, 500),
  });
  const equity = useQuery({
    queryKey: ["model-equity", modelId],
    queryFn: () => fetchEquityCurve(modelId, 1000),
  });

  // State change uses the same backend endpoint regardless of which control
  // the user clicked (Retire, Restore, Promote to Live, Demote, Unpin).
  // Optimistic-feel: invalidate the model-detail query on success so the
  // banner re-fetches with the new state.
  const stateMutation = useMutation({
    mutationFn: updateTradingModel,
    onSuccess: (_resp, vars) => {
      const bits: string[] = [];
      if (vars.state !== undefined) bits.push(`state → ${vars.state}`);
      if (vars.pinned !== undefined) bits.push(vars.pinned ? "pinned" : "unpinned");
      setStateBanner({ ok: true, msg: `Saved: ${bits.join(", ") || "updated"}` });
      queryClient.invalidateQueries({ queryKey: ["model-detail", modelId] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard-for-run-detail"] });
    },
    onError: (err) => {
      setStateBanner({ ok: false, msg: String((err as Error).message || err) });
    },
  });

  const decisionChartData = useMemo(() => {
    const rows: PredictionRow[] = detail.data?.recent_predictions ?? [];
    return rows
      .filter((r) => r.current_price !== undefined && r.current_price !== null)
      .map((r) => ({
        ts: r.decision_ts,
        symbol: r.symbol,
        price: Number(r.current_price),
        action: Number(r.ensemble_action ?? r.individual_action ?? 0),
        signal: signalFor(r.signal_type, r.ensemble_action ?? r.individual_action),
        pnl: r.pnl_pct !== undefined && r.pnl_pct !== null ? Number(r.pnl_pct) : null,
      }))
      .sort((a, b) => a.ts.localeCompare(b.ts));
  }, [detail.data]);

  if (detail.isLoading) return <div>Loading…</div>;
  if (detail.error) return <div className="card" style={{ color: "#fca5a5" }}>{String(detail.error)}</div>;
  if (!detail.data) return <div>Not found</div>;

  const perf = detail.data.performance;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Link to="/" className="backlink">← Leaderboard</Link>
        {perf.ticker && (
          <Link
            to={`/symbols/${encodeURIComponent(perf.ticker)}?tab=day`}
            style={{
              fontSize: 13,
              padding: "6px 12px",
              background: "#1e3a8a",
              color: "#e2e8f0",
              borderRadius: 4,
              textDecoration: "none",
              border: "1px solid #3b82f6",
            }}
          >
            Day view for {perf.ticker} →
          </Link>
        )}
      </div>
      <h2 style={{ marginTop: 12 }}>
        <code>{modelId}</code> {perf.ticker && (
          <Link to={`/symbols/${encodeURIComponent(perf.ticker)}`} style={{ color: "#94a3b8", textDecoration: "none" }}>
            ({perf.ticker})
          </Link>
        )}
      </h2>

      {/* State banner — current trading state + action buttons (admin gates
          on the backend; non-admins will see 403 on click). */}
      {(() => {
        const state = perf.state ?? null;
        const pinned = !!perf.pinned;
        const sk = perf.composite_sk ?? null;
        const colors = stateColors(state);
        const c = colors;
        const cannotEdit = !sk || !perf.ticker;
        const callUpdate = (patch: { state?: "paper-live" | "live" | "retired"; pinned?: boolean }) => {
          if (cannotEdit) return;
          // Build a human-readable confirmation message that names exactly
          // what's about to change so the operator knows the consequences.
          const summary: string[] = [];
          if (patch.state) summary.push(`state → ${patch.state}`);
          if (patch.pinned === true) summary.push("pinned (auto-promote will skip this ticker)");
          if (patch.pinned === false) summary.push("unpinned (auto-promote will resume for this ticker)");
          if (!window.confirm(`Update ${perf.ticker} model?\n\n${summary.join("\n")}`)) return;
          stateMutation.mutate({ ticker: perf.ticker!, sk: sk!, ...patch });
        };

        const btn = (label: string, onClick: () => void, hint: string, color = "#3b82f6") => (
          <button
            disabled={stateMutation.isPending || cannotEdit}
            onClick={onClick}
            title={hint}
            style={{
              fontSize: 12, padding: "5px 10px",
              background: stateMutation.isPending ? "#1e293b" : color,
              color: "#fff",
              border: `1px solid ${color}`, borderRadius: 4,
              cursor: stateMutation.isPending ? "wait" : "pointer", fontWeight: 600,
              opacity: cannotEdit ? 0.45 : 1,
            }}
          >
            {label}
          </button>
        );

        return (
          <div className="card" style={{ borderLeft: `4px solid ${c.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{
                background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
                padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.5,
              }}>
                {state ?? "no TradingModels row"}
              </span>
              {pinned && (
                <span title="Manually promoted from the dashboard. Auto-promote will skip this ticker until you unpin."
                      style={{
                        background: "#3b0764", color: "#e9d5ff", border: "1px solid #a78bfa",
                        padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
                      }}>
                  📌 PINNED
                </span>
              )}
              {perf.in_active_ensemble && (
                <span title="One of the top-N rows currently used by get_active_model for this ticker."
                      style={{
                        background: "#0f3326", color: "#86efac", border: "1px solid #16a34a",
                        padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
                      }}>
                  IN ACTIVE ENSEMBLE
                </span>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                {state === "paper-live" && btn("Promote to live", () => callUpdate({ state: "live" }),
                  "Move to live trading. Real money will trade on this model's signals.", "#16a34a")}
                {state === "live" && btn("Demote to paper-live", () => callUpdate({ state: "paper-live" }),
                  "Stop using real money; the model still runs in paper trading.", "#3b82f6")}
                {state === "retired" && btn("Restore to paper-live", () => callUpdate({ state: "paper-live" }),
                  "Bring this model back into the paper-trading rotation.", "#3b82f6")}
                {state && state !== "retired" && btn("Retire", () => callUpdate({ state: "retired" }),
                  "Remove from active trading. The row stays in DDB but get_active_model ignores it.", "#dc2626")}
                {pinned
                  ? btn("Unpin", () => callUpdate({ pinned: false }),
                      "Let auto-promote replace this ticker's active models again on the next TPE run.", "#a78bfa")
                  : (state === "paper-live" || state === "live") && btn("Pin", () => callUpdate({ pinned: true }),
                      "Lock in this model. Auto-promote will skip this ticker until you unpin.", "#a78bfa")}
              </div>
            </div>
            {cannotEdit && (
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                This model has no TradingModels row (backfill / synthetic row). State controls disabled.
              </div>
            )}
            {stateBanner && (
              <div style={{
                marginTop: 10, padding: "6px 10px", borderRadius: 4, fontSize: 12,
                background: stateBanner.ok ? "#0f3326" : "#3f1d1d",
                color: stateBanner.ok ? "#86efac" : "#fca5a5",
                border: `1px solid ${stateBanner.ok ? "#16a34a" : "#dc2626"}`,
              }}>
                {stateBanner.msg}
              </div>
            )}
          </div>
        );
      })()}

      <div className="card">
        <div className="kv">
          <div><HelpTooltip text={TOOLTIPS.sharpe}><strong>Sharpe (rolling)</strong></HelpTooltip><span>{Number(perf.rolling_sharpe_20 ?? 0).toFixed(3)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.cumulativeReturn}><strong>Cumulative Return</strong></HelpTooltip><span>{fmtPct(perf.cum_return_pct)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.hitRate}><strong>Hit rate</strong></HelpTooltip><span>{fmtPct(perf.hit_rate)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.meanPerPred}><strong>Mean/pred</strong></HelpTooltip><span>{fmtPct(perf.mean_return_per_pred)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.stdPerPred}><strong>Std/pred</strong></HelpTooltip><span>{fmtPct(perf.std_return_per_pred)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.nPredictions}><strong>N predictions</strong></HelpTooltip><span>{perf.total_predictions ?? 0}</span></div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          <HelpTooltip text={TOOLTIPS.decisionsOverTime}>Decisions over time</HelpTooltip>
        </h3>
        {decisionChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={decisionChartData}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fill: "#94a3b8", fontSize: 10 }} hide />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                domain={["auto", "auto"]}
                tickFormatter={(v) => `$${Number(v).toFixed(2)}`}
              />
              <Tooltip
                contentStyle={{ background: "#0b1220", border: "1px solid #1e293b" }}
                labelFormatter={(l) => String(l)}
                formatter={(_v, _name, item) => {
                  const p = item.payload as { price: number; action: number; signal: string; pnl: number | null };
                  const pnl = p.pnl === null ? "—" : `${(p.pnl * 100).toFixed(2)}%`;
                  return [`$${p.price.toFixed(2)}  ${p.signal} (${p.action.toFixed(3)})  P&L ${pnl}`, "decision"];
                }}
              />
              <Line
                type="monotone"
                dataKey="price"
                stroke="#3b82f6"
                strokeWidth={1.5}
                isAnimationActive={false}
                dot={(props: { cx?: number; cy?: number; payload?: { ts: string; symbol: string; signal: "BUY" | "SELL" | "HOLD" } }) => {
                  const { cx, cy, payload } = props;
                  if (cx === undefined || cy === undefined || !payload) {
                    return <g key="empty" />;
                  }
                  return (
                    <circle
                      key={`${payload.symbol}-${payload.ts}`}
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill={signalColor(payload.signal)}
                      stroke="#0f172a"
                      strokeWidth={1}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          `/replay/${encodeURIComponent(payload.symbol)}/${encodeURIComponent(payload.ts)}`,
                        )
                      }
                    />
                  );
                }}
                activeDot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: "#94a3b8" }}>No decisions logged for this model yet.</div>
        )}
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
          Click a dot to open the full replay. Green = long, red = short, gray = flat.
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          <HelpTooltip text={TOOLTIPS.counterfactualEquityCurve}>Counterfactual equity curve</HelpTooltip>
        </h3>
        {equity.data && equity.data.points.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={equity.data.points}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="decision_ts" tick={{ fill: "#94a3b8", fontSize: 11 }} hide />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(1)}%`} />
              <Tooltip
                contentStyle={{ background: "#0b1220", border: "1px solid #1e293b" }}
                formatter={(v: number) => `${(v * 100).toFixed(2)}%`}
                labelFormatter={(l) => String(l)}
              />
              <Line type="monotone" dataKey="cum_return_pct" stroke="#60a5fa" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ color: "#94a3b8" }}>No equity curve yet — needs filled predictions.</div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Recent predictions</h3>
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Symbol</th>
              <th>
                <HelpTooltip text="BUY / SELL / HOLD as decided by the trading code (signal_type field on the row, or fallback derived from action magnitude).">Signal</HelpTooltip>
              </th>
              <th className="num"><HelpTooltip text={TOOLTIPS.action}>Action</HelpTooltip></th>
              <th className="num"><HelpTooltip text={TOOLTIPS.ensemble}>Ensemble</HelpTooltip></th>
              <th className="num"><HelpTooltip text={TOOLTIPS.current}>Current</HelpTooltip></th>
              <th className="num"><HelpTooltip text={TOOLTIPS.next}>Next</HelpTooltip></th>
              <th className="num"><HelpTooltip text={TOOLTIPS.pnl}>P&L</HelpTooltip></th>
            </tr>
          </thead>
          <tbody>
            {/* Group by date (the decision_ts's calendar day) so a date
                header separates each day's predictions. Newest day on top,
                newest prediction within a day on top.  We render this as
                interleaved <tr> rows (date header rows + data rows) inside
                a single <tbody> so the table layout stays consistent. */}
            {(() => {
              const sorted = [...detail.data.recent_predictions].sort((a, b) =>
                b.decision_ts.localeCompare(a.decision_ts),
              );
              const rows: JSX.Element[] = [];
              let currentDate: string | null = null;
              for (const p of sorted) {
                const dateKey = p.decision_ts.slice(0, 10);
                if (dateKey !== currentDate) {
                  currentDate = dateKey;
                  // Count this day's predictions for the header label.
                  const dayCount = sorted.filter((r) => r.decision_ts.startsWith(dateKey)).length;
                  // The date is a link straight to the symbol's day view for
                  // that date — saves a trip back through the leaderboard.
                  // Requires perf.ticker, which is on the leaderboard row for
                  // this model. Fall back to plain text if it's missing.
                  const dayHref = perf.ticker
                    ? `/symbols/${encodeURIComponent(perf.ticker)}?tab=day&date=${dateKey}`
                    : null;
                  rows.push(
                    <tr key={`hdr-${dateKey}`} style={{ background: "#0f172a" }}>
                      <td
                        colSpan={8}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "10px 12px",
                          borderTop: "1px solid #334155",
                          borderBottom: "1px solid #1e293b",
                          letterSpacing: 0.3,
                        }}
                      >
                        {dayHref ? (
                          <Link
                            to={dayHref}
                            style={{ color: "#93c5fd", textDecoration: "none" }}
                            title="Open the day view for this date"
                          >
                            {dateKey}
                          </Link>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>{dateKey}</span>
                        )}{" "}
                        <span style={{ color: "#64748b", fontWeight: 400 }}>· {dayCount} prediction{dayCount === 1 ? "" : "s"}</span>
                      </td>
                    </tr>,
                  );
                }
                const sig = signalFor(p.signal_type, p.ensemble_action ?? p.individual_action);
                rows.push(
                  <tr key={`${p.symbol}-${p.decision_ts}-${p.model_id}`}>
                    <td style={{ fontSize: 12 }}>
                      <Link to={`/replay/${encodeURIComponent(p.symbol)}/${encodeURIComponent(p.decision_ts)}`}>
                        {/* Within a day-group, just show the time-of-day —
                            the date is already in the header above. */}
                        {p.decision_ts.slice(11, 19)}
                      </Link>
                    </td>
                    <td>{p.symbol}</td>
                    <td style={{ color: signalColor(sig), fontWeight: 600 }}>{sig}</td>
                    <td className="num">{Number(p.individual_action ?? 0).toFixed(4)}</td>
                    <td className="num">{Number(p.ensemble_action ?? 0).toFixed(4)}</td>
                    <td className="num">{p.current_price !== undefined ? Number(p.current_price).toFixed(2) : "—"}</td>
                    <td className="num">{p.next_close !== undefined ? Number(p.next_close).toFixed(2) : "—"}</td>
                    <td className={`num ${p.pnl_pct === undefined ? "" : Number(p.pnl_pct) > 0 ? "pos" : "neg"}`}>
                      {p.pnl_pct !== undefined ? fmtPct(Number(p.pnl_pct)) : "—"}
                    </td>
                  </tr>,
                );
              }
              return rows;
            })()}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
